<script setup lang="ts">
import { APP_TABS_DATA } from '~/constants/page-constants'
import type { AppDetailsResponse } from '~/server/api/apps/[slug]'
import type { BreadcrumbLink } from '~/types/product.model'
import type { LinkTrackingData } from '~/types/tracking'

/**
 * We do not want initial navigation to have smooth scroll,
 * but want to enable it for scrolling to tabs (immediately after navigation)
 */
useSmoothScrolling(false)
onMounted(() => {
  setTimeout(() => {
    useSmoothScrolling(true)
  }, 100)
})
const route = useRoute()
const { origin } = useRequestURL()

const { data, error } = await useFetch<AppDetailsResponse>(
  `/api/apps/${route.params.slug}`,
)

useShowErrorPage(error)

usePage({
  metaTitle: data.value?.seo_meta_title || 'App page',
  metaDescription: data.value?.seo_meta_description || 'App page description',
  metaImageUrl:
    data.value?.logo_url || getIconSrc('og-logo-image.png', { origin }),
  trackingPage: 'App Details page',
  trackingPageTitle: `App: ${data.value?.title || 'Unknown'}`,
  trackingSourceId: data.value?.id,
})

useAppDetailsPageSchema(data.value)
const hasMedia = computed(() => Boolean(data.value?.media?.length))

const breadcrumbLinks = computed<BreadcrumbLink[]>(() => {
  const appData = data.value
  if (!appData) {
    return []
  }

  const shortCategoryUrl = getCategoryUrl(appData.category.slug)
  // use for tracking only, because clicking on it leads to non-SPA navigation
  const fullCategoryUrl = getCategoryUrl(appData.category.slug, origin)
  return [
    {
      name: appData.category.title,
      url: shortCategoryUrl,
      trackingData: {
        linkType: 'Category',
        linkUrl: fullCategoryUrl,
        destinationId: appData.category.slug,
        destinationTitle: appData.category.title,
      },
    },
    {
      name: appData.title,
    },
  ]
})

const trackLinkClick = useTrackLinkClick()

/**
 * Function intercepts clicks on links in details (HTMLRenderer content)
 * And opens them in a new tab.
 * (since we can not put target="_blank" on them, we do it using js)
 */
const handleDetailsClick = (
  event: MouseEvent,
  linkTrackingType: LinkTrackingData['linkType'],
): void => {
  const eventTarget = event.target
  if (!(eventTarget instanceof HTMLAnchorElement) || !eventTarget.href) {
    return
  }

  trackLinkClick({
    pageLocation: 'Main Content',
    linkType: linkTrackingType,
    linkUrl: eventTarget.href,
    ...(eventTarget.textContent && {
      destinationTitle: eventTarget.textContent,
    }),
  })

  event.preventDefault()
  window.open(eventTarget.href, '_blank')
}
</script>

<template>
  <div>
    <Breadcrumbs
      v-if="data?.category?.title"
      :links="breadcrumbLinks"
    />
    <article
      v-if="data"
      class="app-details"
    >
      <AppDetailsHeader :app-data="data" />

      <AppDetailsTabs :description="data.description" />

      <div class="app-details__grid">
        <div class="app-details__main">
          <h2
            :id="APP_TABS_DATA.overview.id"
            class="mb-32 heading-5"
          >
            About {{ data.title }}
          </h2>

          <UiUniqueSellingPoints
            v-if="data.selling_points"
            :usps="data.selling_points"
            :class="{ 'mb-32': !hasMedia }"
          />

          <AppDetailsImages
            v-if="hasMedia && data.media"
            :media="data.media"
          />

          <div
            v-if="data.description?.overview"
            class="app-details__description"
          >
            <p>{{ data.description.overview }}</p>
          </div>
        </div>
        <div class="app-details__tabs">
          <HTMLRenderer
            v-if="data.description?.how_to_install"
            :id="APP_TABS_DATA.howToConnect.id"
            :data-test="APP_TABS_DATA.howToConnect.id"
            :title="APP_TABS_DATA.howToConnect.title"
            :content="data.description.how_to_install"
            class="mb-32"
            @click="
              handleDetailsClick(
                $event,
                `${APP_TABS_DATA.howToConnect.title} link`,
              )
            "
          />
          <HTMLRenderer
            v-if="data.description?.resources_support"
            :id="APP_TABS_DATA.resourcesAndSupport.id"
            :data-test="APP_TABS_DATA.resourcesAndSupport.id"
            :title="APP_TABS_DATA.resourcesAndSupport.title"
            :content="data.description.resources_support"
            class="mb-32"
            @click="
              handleDetailsClick(
                $event,
                `${APP_TABS_DATA.resourcesAndSupport.title} link`,
              )
            "
          />
          <HTMLRenderer
            v-if="data.description?.privacy_terms"
            :id="APP_TABS_DATA.privacyAndTerms.id"
            :data-test="APP_TABS_DATA.privacyAndTerms.id"
            :title="APP_TABS_DATA.privacyAndTerms.title"
            :content="data.description?.privacy_terms"
            class="mb-32"
            @click="
              handleDetailsClick(
                $event,
                `${APP_TABS_DATA.privacyAndTerms.title} link`,
              )
            "
          />
          <HTMLRenderer
            v-if="data.description?.carrier_links"
            :id="APP_TABS_DATA.carrierLinks.id"
            :data-test="APP_TABS_DATA.carrierLinks.id"
            :title="APP_TABS_DATA.carrierLinks.title"
            :content="data.description?.carrier_links"
            class="mb-32"
            @click="
              handleDetailsClick(
                $event,
                `${APP_TABS_DATA.carrierLinks.title} link`,
              )
            "
          />

          <AppDetailsReviews
            :tab-id="APP_TABS_DATA.reviews.id"
            :data="data"
          />
        </div>
        <AppDetailsExtraFeatures
          :data="data"
          class="app-details__sidebar"
        />
      </div>
    </article>
  </div>
</template>

<style lang="scss" scoped>
.app-details {
  display: grid;
  gap: var(--app-spacer-30);
}

.app-details__grid {
  display: grid;
  gap: var(--app-spacer-30);
  grid-template-areas:
    'main'
    'sidebar'
    'tabs';
  grid-template-columns: 1fr;

  @include min-width('lg') {
    // we need '. sidebar' otherwise `main` stretches as sidebar grows
    grid-template-areas:
      'main sidebar'
      'tabs sidebar'
      '. sidebar';
    grid-template-columns: 3fr 1fr;
  }
}

.app-details__main {
  grid-area: main;
  min-width: 0;
}

.app-details__tabs {
  grid-area: tabs;
}

.app-details__sidebar {
  grid-area: sidebar;
}
</style>
